import { getPlaceSearchSuggestions } from "@/api/places";

export const MIN_PLACE_SUGGESTION_SEARCH_LENGTH = 2;

export const buildPlaceSuggestionParams = ({
  searchTerm,
  maxResults = 4,
  offset = 0,
  nearbyOnly = false,
  userLocation,
  mode = "quick",
  includeExecutionMeta = false,
  withCover = false,
  // 「혹시 …를 찾으시나요?」를 눌렀을 때 지역을 콕 짚는다.
  areaPick = "",
}) => ({
  search: String(searchTerm || "").trim(),
  maxResults,
  ...(offset > 0 ? { offset } : {}),
  includeExecutionMeta: includeExecutionMeta ? "true" : "false",
  ...(mode ? { mode } : {}),
  ...(withCover ? { withCover: "true" } : {}),
  ...(areaPick ? { areaPick } : {}),
  // 위치는 알면 항상 보낸다. 서버가 이걸로 가까운 곳을 먼저 고른다.
  //
  // 예전에는 '내 주변' 모드일 때만 보냈다. 그래서 서울에서 "갈비"를 쳐도 서버는
  // 어디서 찾는지 모른 채 전국에서 8곳을 골랐고, 대구 가게가 1등으로 올라왔다.
  // 거리는 화면에서 뒤늦게 계산해 붙이기만 했다.
  ...(userLocation?.lat && userLocation?.lng
    ? {
        lat: userLocation.lat,
        lng: userLocation.lng,
      }
    : {}),
});

export const getPlaceSuggestionRequestKey = ({
  searchTerm,
  nearbyOnly = false,
  userLocation,
}) =>
  [
    String(searchTerm || "").trim().toLowerCase(),
    nearbyOnly ? "nearby" : "global",
    // 위치가 결과 순서를 바꾸므로 요청 구분에도 넣는다.
    userLocation?.lat ? Number(userLocation.lat).toFixed(3) : "",
    userLocation?.lng ? Number(userLocation.lng).toFixed(3) : "",
  ].join(":");

export const isRoutablePlaceSuggestion = (place) =>
  Boolean(
    place?.canonicalPath ||
      place?.publicId ||
      place?.placeUniqueNumber ||
      place?.contentId ||
      place?.contentid
  );

export const extractPlaceSuggestions = (response) =>
  Array.isArray(response?.placeSuggestions)
    ? response.placeSuggestions.filter(isRoutablePlaceSuggestion)
    : [];

export const extractPostSearchSuggestions = (response) =>
  Array.isArray(response?.postSearchSuggestions)
    ? response.postSearchSuggestions
    : [];

export const fetchPlaceSuggestionResults = async ({
  searchTerm,
  maxResults = 4,
  offset = 0,
  nearbyOnly = false,
  userLocation,
  includeExecutionMeta = false,
  withCover = false,
  // 「혹시 …를 찾으시나요?」를 눌렀을 때 지역을 콕 짚는다.
  areaPick = "",
}) => {
  const nextSearch = String(searchTerm || "").trim();

  if (nextSearch.length < MIN_PLACE_SUGGESTION_SEARCH_LENGTH) {
    return {
      placeSuggestions: [],
      postSearchSuggestions: [],
    };
  }

  const quickResponse = await getPlaceSearchSuggestions(
    buildPlaceSuggestionParams({
      searchTerm: nextSearch,
      maxResults,
      offset,
      nearbyOnly,
      userLocation,
      areaPick,
      includeExecutionMeta,
      withCover,
    })
  );
  const quickPlaceSuggestions = extractPlaceSuggestions(quickResponse);

  if (quickPlaceSuggestions.length > 0) {
    return {
      ...quickResponse,
      placeSuggestions: quickPlaceSuggestions,
      postSearchSuggestions: extractPostSearchSuggestions(quickResponse),
    };
  }

  const fallbackResponse = await getPlaceSearchSuggestions(
    buildPlaceSuggestionParams({
      searchTerm: nextSearch,
      maxResults,
      offset,
      nearbyOnly,
      userLocation,
      mode: null,
      includeExecutionMeta,
      withCover,
    })
  );

  return {
    ...fallbackResponse,
    placeSuggestions: extractPlaceSuggestions(fallbackResponse),
    postSearchSuggestions: extractPostSearchSuggestions(fallbackResponse),
  };
};
